import { cx } from 'linaria';
import * as React from 'react';

import { IconChevronDown, IconChevronLeft, IconChevronRight, IconChevronUp } from '../foundation/icons';
import { states, Typography, useI18n, useTheme, Variables } from '../index';

import { useOnClickOutside } from '../hooks/use-on-click-outside';
import * as styles from './pagination.styles';

export type ItemsPerPage = 10 | 20 | 50 | 100 | number;

export interface PaginationProps {
    items: number;
    itemsPerPage?: ItemsPerPage;
    defaultIndex?: number;
    pageIndex?: number;
    onChange?: (pageNumber: number, itemsPerPage: ItemsPerPage) => void;
    showPositionText?: boolean;
    __privateSortOptions?: SortOptions[];
}

interface ViewableItems {
    min: number;
    max: number;
}

export const Pagination = (props: PaginationProps) => {
    const { items, defaultIndex = 0, onChange, showPositionText, __privateSortOptions = [] } = props;
    const [pageIndex, setPageIndex] = React.useState<number>(props.pageIndex || defaultIndex);
    const [itemsPerPage, setItemsPerPage] = React.useState<ItemsPerPage>(props.itemsPerPage || 10);
    const [viewableItems, setViewableItems] = React.useState<ViewableItems>({
        min: 1,
        max: 10,
    });

    const theme = useTheme();

    const calculateItemNumber = () => {
        const calcMinItems = pageIndex * itemsPerPage + 1;
        const calcMaxItems = (pageIndex + 1) * itemsPerPage;

        if (calcMinItems > items) {
            const calcNewPageNumber = Math.floor(items / itemsPerPage);

            setPageIndex(calcNewPageNumber);
        } else {
            setViewableItems({
                min: calcMinItems,
                max: calcMaxItems > items ? items : calcMaxItems,
            });
        }
    };

    const handlePrevious = () => {
        const page = pageIndex - 1;
        setPageIndex(page);
        onChange(page, itemsPerPage);
    };

    const handleNext = () => {
        const page = pageIndex + 1;
        setPageIndex(page);
        onChange(page, itemsPerPage);
    };

    const handleSort = (numberOfItems: ItemsPerPage) => {
        if (numberOfItems) {
            setItemsPerPage(numberOfItems);
            onChange(pageIndex, numberOfItems);
        }
    };

    React.useEffect(() => {
        calculateItemNumber();
    }, [items, pageIndex, itemsPerPage]);

    return (
        <div className={cx(styles.root)}>
            {showPositionText && (
                <div className={cx(styles.itemsLabel)}>
                    <Typography variant="caption-1">
                        {viewableItems.min} - {viewableItems.max} of {items} items
                    </Typography>
                </div>
            )}
            <button
                title="Previous Page"
                data-testid="previous-page"
                onClick={handlePrevious}
                disabled={pageIndex === 0 ? true : false}
                className={cx(styles.paginationArrows, theme === 'dark' && styles.paginationDarkArrow, states)}
            >
                <IconChevronLeft width="7" height="12" />
            </button>
            <button
                title="Next Page"
                data-testid="next-page"
                onClick={handleNext}
                disabled={viewableItems.max >= items ? true : false}
                className={cx(styles.paginationArrows, theme === 'dark' && styles.paginationDarkArrow, states)}
            >
                <IconChevronRight width="7" height="12" />
            </button>
            <PaginationSort
                variant={theme}
                value={itemsPerPage}
                onChange={handleSort}
                __privateSortOptions={__privateSortOptions}
            />
        </div>
    );
};

interface PaginationSortProps {
    variant: ReturnType<typeof useTheme>;
    value: ItemsPerPage;
    onChange?: (newValue: ItemsPerPage) => void;
    __privateSortOptions?: SortOptions[];
}

export interface SortOptions {
    label: string;
    value: ItemsPerPage;
}

export const PaginationSort: React.FC<PaginationSortProps> = (props) => {
    const { variant, value, onChange, __privateSortOptions = [] } = props;

    const messages = useI18n();

    const [dropdownVisible, setSortByDropdownVisible] = React.useState(false);

    const listRef = React.useRef();
    useOnClickOutside(listRef, () => setSortByDropdownVisible(false));

    const options = React.useMemo(() => {
        return [
            { label: messages['pagination.per_page_10'], value: 10 },
            { label: messages['pagination.per_page_20'], value: 20 },
            { label: messages['pagination.per_page_50'], value: 50 },
            {
                label: messages['pagination.per_page_100'],
                value: 100,
            },
        ].concat(__privateSortOptions) as SortOptions[];
    }, [__privateSortOptions]);

    // We need to display the label
    const currentLabel = React.useMemo(() => options.find((option) => option.value === value).label, [value, options]);

    return (
        <div className={cx(styles.selectItems)}>
            <div
                data-testid="select-items-button"
                className={cx(
                    styles.itemSelect,
                    dropdownVisible && (variant === 'dark' ? styles.itemSelectActiveDark : styles.itemSelectActive),
                )}
                onClick={() => setSortByDropdownVisible(true)}
            >
                <Typography variant="body-1">{currentLabel}</Typography>
                {dropdownVisible ? (
                    <IconChevronUp width="12" color={Variables.css.textBody} />
                ) : (
                    <IconChevronDown width="12" color={Variables.css.textBody} />
                )}
            </div>
            {dropdownVisible && (
                <div ref={listRef} className={cx(styles.dropdownContainer)} data-testid="item-options">
                    {options.map((option) => (
                        <div
                            key={option.label}
                            className={cx(styles.dropdownOption, option.value === value && styles.dropdownOptionActive)}
                            onClick={() => {
                                onChange(option.value);
                                setSortByDropdownVisible(false);
                            }}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
